import type { AxiosRequestConfig } from 'axios';
import type { Issue, IssueAttachment } from '../types';
import { tapJournal } from '../journal/journalSync';
import { api } from './http';

export const getIssues = (config?: AxiosRequestConfig) =>
  api.get<Issue[]>('/issues', config);
export const getOpenIssues = (config?: AxiosRequestConfig) =>
  api.get<Issue[]>('/issues/open', config);
export const updateIssue = (
  issueId: number,
  data: {
    description: string;
    severity: string;
    issue_date?: string;
    resolved_date?: string;
    resolved_by?: string;
    cost?: number;
    cost_split?: string;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
    comment?: string;
  }
) =>
  tapJournal(
    api.put<Issue>(`/issues/${issueId}`, null, {
      params: {
        description: data.description,
        severity: data.severity,
        ...(data.issue_date ? { issue_date: data.issue_date } : {}),
        ...(data.resolved_date ? { resolved_date: data.resolved_date } : {}),
        ...(data.resolved_by != null ? { resolved_by: data.resolved_by } : {}),
        ...(data.cost != null ? { cost: data.cost } : {}),
        ...(data.cost_split ? { cost_split: data.cost_split } : {}),
        ...(data.payer_ids && data.payer_ids.length
          ? { payer_ids: data.payer_ids.join(',') }
          : {}),
        ...(data.payer_shares && data.payer_shares.length
          ? { payer_shares: data.payer_shares.join(',') }
          : {}),
        ...(data.flight_ids && data.flight_ids.length
          ? { flight_ids: data.flight_ids.join(',') }
          : {}),
        ...(data.comment !== undefined ? { comment: data.comment } : {}),
      },
    })
  );
export const deleteIssue = (issueId: number) =>
  tapJournal(api.delete<{ deleted: boolean }>(`/issues/${issueId}`));
export const addIssue = (data: { 
  user_id: number; 
  description: string; 
  severity: string;
  flight_id?: number | null;
  issue_date?: string;
  comment?: string;
}) => tapJournal(api.post<Issue>('/issues', null, { params: data }));

export const getIssueAttachments = (issueId: number) =>
  api.get<IssueAttachment[]>(`/issues/${issueId}/attachments`);

export const uploadIssueAttachment = (
  issueId: number,
  file: File,
  group_id?: string
) => {
  const form = new FormData();
  form.append('file', file);
  if (group_id) {
    form.append('group_id', group_id);
  }
  return api.post<IssueAttachment>(`/issues/${issueId}/attachments`, form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });
};

export const getIssueAttachmentFile = (issueId: number, attachmentId: number) =>
  api.get<Blob>(`/issues/${issueId}/attachments/${attachmentId}/file`, {
    responseType: 'blob',
  });

export const downloadIssueAttachment = async (
  issueId: number,
  attachmentId: number,
  filename: string
) => {
  const res = await getIssueAttachmentFile(issueId, attachmentId);
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteIssueAttachment = (issueId: number, attachmentId: number) =>
  api.delete<{ deleted: boolean; id: number }>(
    `/issues/${issueId}/attachments/${attachmentId}`
  );
export const resolveIssue = (
  issue_id: number,
  data: {
    resolved_date: string;
    resolved_by: string;
    cost: number;
    cost_split?: string;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
  }
) =>
  tapJournal(
    api.put<{ resolved: boolean }>(`/issues/${issue_id}/resolve`, null, {
      params: {
        resolved_date: data.resolved_date,
        resolved_by: data.resolved_by,
        cost: data.cost,
        cost_split: data.cost_split || 'shares',
        ...(data.payer_ids && data.payer_ids.length
          ? { payer_ids: data.payer_ids.join(',') }
          : {}),
        ...(data.payer_shares && data.payer_shares.length
          ? { payer_shares: data.payer_shares.join(',') }
          : {}),
        ...(data.flight_ids && data.flight_ids.length
          ? { flight_ids: data.flight_ids.join(',') }
          : {}),
      },
    })
  );
